import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react"
import { Cancel01Icon, Cursor01Icon, Globe02Icon, TestTube01Icon, UserMultipleIcon } from "@hugeicons/core-free-icons"
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { usePlanUsage } from '@/hooks/usePlanUsage'
import { getPlanById } from '@/lib/constants/pricing'
import type { PlanUsageCounter } from '@/lib/types/billing'
import { cn } from '@/lib/utils'
import { inferPlanLimitReason } from './planLimitCopy'
import { getNextPlanTier } from './eventQuota'

interface PlanLimitBannerProps {
  title: string
  description: string
  /**
   * Plural noun ("funnels", "A/B tests"). Picks the icon, the usage counter for
   * the "Plan limit · 5 of 5" chip, and the pricing-modal reason.
   */
  noun?: string
  /** Kept for callers; the plan is named in their `title` copy. */
  planName?: string
  /** Shown when provided; omit for a non-dismissible banner. */
  onDismiss?: () => void
  /**
   * Whether the requesting user is the subscription owner
   * (`usePlanUsage().metadata.isOwner`). Defaults to true. Owners get the
   * Upgrade CTA; non-owner members see a "contact your account owner"
   * message instead, since they can't act on it.
   */
  isOwner?: boolean
  className?: string
}

type CounterKey = 'funnels' | 'heatmaps' | 'splitTests' | 'teamSeats' | 'websites'

const NOUN_COUNTER: Record<string, CounterKey> = {
  funnels: 'funnels',
  heatmaps: 'heatmaps',
  experiments: 'splitTests',
  'A/B tests': 'splitTests',
  'team members': 'teamSeats',
  seats: 'teamSeats',
  sites: 'websites',
}

const NOUN_ICON: Record<CounterKey, IconSvgElement | null> = {
  funnels: null, // drawn below, matches the sidebar funnel mark
  heatmaps: Cursor01Icon,
  splitTests: TestTube01Icon,
  teamSeats: UserMultipleIcon,
  websites: Globe02Icon,
}

export interface PlanLimitBannerViewProps {
  title: string
  description: string
  /** "Plan limit · 5 of 5"; omitted when the count isn't known. */
  chip?: string | null
  counter?: CounterKey
  cta?: { label: string; onClick: () => void } | null
  ownerNote?: string | null
  onDismiss?: () => void
  className?: string
}

/**
 * Amber plan-limit banner (Paper "Funnels · Plan limit reached"): names the
 * plan, says it is a limit and not an error, gives the count and one ink
 * upgrade button. Presentational; `PlanLimitBanner` wires it to the account.
 */
export function PlanLimitBannerView({
  title,
  description,
  chip,
  counter = 'funnels',
  cta,
  ownerNote,
  onDismiss,
  className,
}: PlanLimitBannerViewProps) {
  const icon = NOUN_ICON[counter]
  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-x-3.5 gap-y-3 rounded-xl bg-urgency-amber-bg py-3.5 pl-3.5 pr-4 shadow-[0_0_0_1px_color-mix(in_oklab,var(--urgency-amber-accent)_35%,transparent)] sm:flex-nowrap',
        className,
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-card text-urgency-amber-fg shadow-[0_0_0_1px_color-mix(in_oklab,var(--urgency-amber-accent)_35%,transparent)]">
        {icon ? (
          <HugeiconsIcon icon={icon} size={16} />
        ) : (
          <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden>
            <rect x="2.5" y="3.5" width="15" height="3" rx="1.5" fill="currentColor" />
            <rect x="5" y="8.5" width="10" height="3" rx="1.5" fill="currentColor" />
            <rect x="7.5" y="13.5" width="5" height="3" rx="1.5" fill="currentColor" />
          </svg>
        )}
      </span>
      <div className="flex min-w-0 flex-1 basis-[calc(100%-46px)] flex-col gap-[3px] sm:basis-auto">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm font-semibold leading-[18px] text-foreground">{title}</p>
          {chip && (
            <span className="rounded-full bg-card px-2 py-0.5 text-xs font-medium leading-4 text-urgency-amber-fg shadow-[0_0_0_1px_color-mix(in_oklab,var(--urgency-amber-accent)_35%,transparent)]">
              {chip}
            </span>
          )}
        </div>
        <p className="text-[13px] leading-[18px] text-muted-foreground">{description}</p>
      </div>
      {cta ? (
        <button
          type="button"
          onClick={cta.onClick}
          className="inline-flex h-8 shrink-0 items-center rounded-lg bg-urgency-ink-bg px-3 text-[13px] font-medium leading-4 text-white transition-colors hover:bg-urgency-ink-bg/90"
        >
          {cta.label}
        </button>
      ) : ownerNote ? (
        <span className="max-w-[180px] shrink-0 text-right text-xs text-muted-foreground">{ownerNote}</span>
      ) : null}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="flex size-6 shrink-0 items-center justify-center rounded-md text-urgency-amber-fg transition-colors hover:bg-black/5"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={14} />
        </button>
      )}
    </div>
  )
}

function limitChip(counter: PlanUsageCounter | undefined): string | null {
  if (!counter || counter.isUnlimited || !Number.isFinite(counter.limit)) return null
  return `Plan limit · ${counter.current} of ${counter.limit}`
}

/**
 * Plan-limit banner for a create surface at its cap. Not shown after a cancel:
 * the read-only strip under the header already explains that, and the create
 * buttons swap to Upgrade with a "Your subscription has ended" tooltip.
 */
export function PlanLimitBanner({
  title,
  description,
  noun = 'items',
  onDismiss,
  isOwner = true,
  className,
}: PlanLimitBannerProps) {
  const { openPricingModal, isReadOnly, currentPlan } = useSubscription()
  const usage = usePlanUsage()
  if (isReadOnly) return null

  const counterKey = NOUN_COUNTER[noun]
  const next = getNextPlanTier(currentPlan)
  const nextName = next ? getPlanById(next)?.name : null
  const reason = inferPlanLimitReason(noun)

  return (
    <PlanLimitBannerView
      title={title}
      description={description}
      chip={counterKey ? limitChip(usage[counterKey]) : null}
      counter={counterKey}
      cta={
        isOwner
          ? {
              label: nextName ? `Upgrade to ${nextName}` : 'Compare plans',
              onClick: () => openPricingModal(reason ? { reason } : undefined),
            }
          : null
      }
      ownerNote={isOwner ? null : 'Ask your account owner to upgrade'}
      onDismiss={onDismiss}
      className={className}
    />
  )
}
